import {
  convert,
  newDate,
  getFields,
  score,
  extractKeys,
  strIndex
} from '@/sui/data/flatten/tools'

import {
  patientAge,
  addMO,
  bmrMO,
  addBMRBHreMO,
  patientDays,
  findSurveillance,
  initializeSurveillance,
  origin,
  addBacteremia,
  addPneumopathy,
  divDuration,
  ktDuration,
  unitType
} from '@/sui/data/flatten/common'

function addKT (prefix, target, parent, kt, v) {
  const ktt = v === 1 ? c => c.type : c => c.type_v2
  const config = [
    { key: 'type', get: ktt },
    { key: 'site', get: c => c.insertionSite },
    { key: 'insertion', get: c => newDate(c.insertion) },
    { key: 'removed', get: c => c.removed },
    { key: 'removal', get: c => newDate(c.removal) },
    { key: 'duration', get: c => ktDuration(parent, c) },
    { key: 'laboratory', get: c => c.laboratory },
    { key: 'col_ilc_blc', get: c => c.isColIlcBlc },
    { key: 'col_ilc_blc_date', get: c => newDate(c.colIlcBlc) }
  ]

  if (v >= 2) {
    config.push(
      { key: 'col_ilc_blc_diag_date', get: c => newDate(c.diagDate) }
    )
  }

  if (v >= 4) {
    config.push(
      { key: 'blc_origin', get: c => c.origin }
    )
  }

  if (v >= 3) {
    config.push(
      { key: 'blc_manip', get: c => c.previousManip },
      { key: 'blc_manip_type', get: c => c.previousManipType },
      { key: 'blc_manip_date', get: c => newDate(c.previousManipDate) }
    )
  }

  if (v >= 2) {
    config.push(
      { key: 'col_ilc_blc_lipids', get: c => c.lipids },
      { key: 'col_ilc_blc_covid', get: c => c.covidStatus },
      { key: 'blc_criteria', get: c => c.divDiag }
    )
  }

  if (v >= 3) {
    config.push(

      { key: 'blc_clinical_signs', get: c => c.clinicalSigns },
      { key: 'blc_bio_signs', get: c => c.bioSigns },
      { key: 'blc_adapted_antibio', get: c => c.adaptedAntibio },
      { key: 'blc_two_hemocs', get: c => c.twoHemocs }
    )
  }

  getFields(config, prefix, target, kt)
  if (kt.isColIlcBlc === 4) {
    ++target.kt_bact_count
  }
  if (kt.type === 1 || kt.type_v2 === 'C1') {
    target.kt_has_cvc = true
  } else if (kt.type === 6 || kt.type_v2 === 'C7') {
    target.kt_has_other = true
  }
  kt.microOrganisms.forEach(
    (mo, i) => addMO(`${prefix}_99_mo${strIndex(i + 1)}`, target, mo, v >= 3, v >= 5 ? 'v2' : 'v1')
  )
}

export function intubDuration (p) {
  return divDuration(
    p, p => p.intubStart, [p => p.intubEnd, p => p.exit, p => p.death]
  )
}

export function urinaryDuration (p) {
  return divDuration(
    p, p => p.urinaryStart, [p => p.urinaryEnd, p => p.exit, p => p.death])
}

function initializePatient (p, s, v, y) {
  const result = {
    type: 'reaInfADI',
    surv_id: p.sv
  }

  initializeSurveillance(result, s)

  result.sheet_id = p.id
  result.v = p.v
  result.st = p.st
  result.unit_type = unitType(s.year, p.f.unitType)
  result.uf = p.f.uf
  result.sheet_number = p.f.sheetNumber
  result.birthdate = newDate(p.f.birthdate)
  result.age_years = patientAge(p.f, y)
  result.age_days = patientDays(p.f, y)
  result.sex = p.f.sex
  result.entry = newDate(p.f.entry)
  if (v === 1) {
    result.origin = origin(p.f.origin)
  } else {
    result.origin = p.f.origin
  }
  result.trauma = p.f.trauma
  result.diagnosis = p.f.diagnosis
  result.immuno = p.f.immuno
  result.antibio = p.f.antibio
  if (v >= 2) {
    result.cancer = p.f.cancer
  }
  result.igsII = score('igsII', p.f.mainSeverity)
  result.pimII = score('pimII', p.f.mainSeverity)
  result.other_score_name = p.f.otherSeverity.code
  result.other_score_value = p.f.otherSeverity.value
  result.dead = p.f.dead
  result.death = newDate(p.f.death)
  result.exit = newDate(p.f.exit)
  if (v >= 3) {
    result.covid = p.f.covidStatus
  }
  result.ecmo = p.f.ecmo
  result.intubated = p.f.intubated
  result.intubation_start = newDate(p.f.intubStart)
  result.intubation_end = newDate(p.f.intubEnd)
  result.intubation_duration = intubDuration(p.f)
  result.reintubated = p.f.reintubated
  result.urinary = p.f.urinary
  result.urinary_start = newDate(p.f.urinaryStart)
  result.urinary_end = newDate(p.f.urinaryEnd)
  result.urinary_duration = urinaryDuration(p.f)
  result.kt_count = p.f.catheters.length
  result.kt_bact_count = 0
  result.kt_has_cvc = false
  result.kt_has_other = false
  result.bact_count = p.f.bacteremias.length
  result.pneu_count = p.f.pneumopathies.length
  if (v >= 5) {
    result.bmi = p.f.bmi
  }
  result.portage_bmr_bhre = p.f.portageBmrBhre
  if (v === 1) {
    result.portage_bmr_bhre_mo = bmrMO(p.f.bmrBhreMicroOrg)
  }

  return result
}

const reducer = (v, y, keys) => (result, p) => {
  const s = findSurveillance(result, p)
  if (s != null && result.p.onlyClosed && s.status !== 'closed') {
    --result.r.length
    return result
  }

  const patient = initializePatient(p, s, v, y)
  if (result.keys == null) {
    result.keys = Object.keys(patient)
  }

  if (v >= 2) {
    p.f.bmrBhreMicroOrgs.forEach(
      (c, i) => addBMRBHreMO(
        `portage_bmr_bhre_mo${strIndex(i + 1)}`, patient, p.f, c
      )
    )
  }

  p.f.catheters.forEach(
    (c, i) => addKT(`kt${strIndex(i + 1)}`, patient, p.f, c, v)
  )
  const bv = [1, 2, 3, 3, 4, 4]
  p.f.bacteremias.forEach(
    (b, i) => addBacteremia(`bact${strIndex(i + 1)}`, patient, p.f, b, bv[v - 1])
  )
  const pv = [1, 2, 2, 2, 3, 3]
  const hasOrigin = v >= 6
  p.f.pneumopathies.forEach(
    (p, i) => addPneumopathy(`pneu${strIndex(i + 1)}`, patient, p.f, p, pv[v - 1], hasOrigin)
  )
  extractKeys(result, patient, keys)
  result.r[result.i++] = patient
  return result
}

function run (v, y) {
  const keys = v === 1 ? ['kt', 'bact', 'pneu']
    : ['portage_bmr_bhre_mo', 'kt', 'bact', 'pneu']
  return (params) => convert(params, reducer(v, y, keys), keys)
}

export default {
  // 2019 sheets are migrated to 2020
  '2019': run(2, 2019),
  '2020': run(2, 2020),
  '2021': run(3, 2021),
  '2022': run(4, 2022),
  '2023': run(5, 2023),
  '2024': run(5, 2024),
  '2025': run(5, 2025),
  '2026': run(6, 2026)
}
